import { isDevMode, Injectable } from '@angular/core';
import { addRxPlugin } from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { RxCollection, RxDatabase, RxError, createRxDatabase, removeRxDatabase } from 'rxdb/plugins/core';
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { environment } from 'environments/environment';

@Injectable({
    providedIn: 'root'
})
export class RxdbInitializerService
{
    static VERSION: number = 0;

    readonly dbName: string = 'iapplydb';

    readonly applicationEmptyDbColl: string = 'application-empty';
    readonly currentSessionUserColl: string = 'current-session-user';
    readonly externalPopulatedFieldsColl: string = 'external-populated-fields';
    readonly organisationDbColl: string = 'organisation';
    readonly productEntitlementsColl: string = 'product-entitlements';
    readonly productMenuColl: string = 'product-menu';
    readonly productsColl: string = 'products';
    readonly offlineApplicationColl: string = 'offline-application';
    readonly surveyColl: string = 'survey';
    readonly uploadFileDbColl: string = 'uploaded-files';
    readonly validatorsColl: string = 'validators';
    readonly vaultProductColl: string = 'vault-products';

    private _dbPr: Promise<RxDatabase>;

    constructor() {
        this.initPlugins();
        this._dbPr = this.initDb();
    }

    private async initPlugins() {
        addRxPlugin(RxDBQueryBuilderPlugin);
        addRxPlugin(RxDBCleanupPlugin);
        addRxPlugin(RxDBLeaderElectionPlugin);

        // if (environment.name !== 'development') { enableProdMode(); }
        // if (isDevMode()) {
        if (environment.name === 'development') {
            await import('rxdb/plugins/dev-mode').then(
                module => addRxPlugin(module.RxDBDevModePlugin)
            );
        }
    }

    public async initDb(): Promise<RxDatabase> {
        let db: RxDatabase = null;
        try {
            db = await createRxDatabase({
                name: this.dbName, // the name of the database
                storage: getRxStorageDexie(),
                ignoreDuplicate: false, // isDevMode(), // TODO!: ignoreDuplicate is only allowed in DEV mode and should never be used in production!
            });

            await this.initCollections(db);

            return db;

        } catch (err) {
            if ((<RxError>err).code == "DB6") {
                removeRxDatabase(this.dbName, getRxStorageDexie());
                await this.initCollections(db);
                return;
            }

            throw err;
        }
    }

    public async truncateAllDbData() {
        for (let item of this.collectionMap) {
            await (await this._dbPr)[item.name].find({}).remove();
            await (await this._dbPr)[item.name].cleanup(0);
        }
    }

    private async initCollections(db: RxDatabase)
    {
        // let all = [];
        // for (let item of this.collectionMap) {
        //     all.push(this.initCollection(db, item.name, item.schema));
        // }
        // await Promise.all(all);

        for (let item of this.collectionMap) {
            try {
                await this.initCollection(db, item.name, item.schema);
            } catch (ex) {
                await db[item.name].remove();
                await this.initCollection(db, item.name, item.schema);
            }
        }
    }

    private async initCollection(db: RxDatabase, collectionName: string, schema: any) {
        let obj = {};
        obj[collectionName] = {
            schema: schema
        };

        // add collections
        try {
            return db.addCollections(obj);
        } catch (ex) {
            (await this._dbPr)[collectionName].remove();
            return db.addCollections(obj);
        }
    }

    public async getVaultProductsCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.vaultProductColl];
    }

    public async getValidatorsCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.validatorsColl];
    }

    public async getProductEntitlementsCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.productEntitlementsColl];
    }

    public async getApplicationEmptyCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.applicationEmptyDbColl];
    }

    public async getSurveyCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.surveyColl];
    }

    public async getProductCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.productsColl];
    }

    public async getExternalPopulatedFieldsCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.externalPopulatedFieldsColl];
    }

    public async getOrganisationCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.organisationDbColl];
    }

    public async getProductMenuCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.productMenuColl];
    }

    public async getCurrentSessionUserCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.currentSessionUserColl];
    }

    public async getOfflineApplicationCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.offlineApplicationColl];
    }

    public async getUploadFileCollection(): Promise<RxCollection> {
        return (await this._dbPr)[this.uploadFileDbColl];
    }

    private readonly applicationEmptySchema = {
        title: "application empty schema",
        version: RxdbInitializerService.VERSION, // <- incremental version-number
        description: "application empty schema",
        primaryKey: 'id',                        // <- 'name' is the primary key for the collection, it must be unique, required and of the type string
        type: "object",
        properties: {
            id: {
                type: "string",
                maxLength: 150
            },
            formId: {
                type: "string"
            },
            formVersion: {
                type: "number"
            },
            userId: {
                type: "string"
            },
        }
    };

    private readonly organisationSchema = {
        title: "organisation schema",
        version: RxdbInitializerService.VERSION,
        description: "organisation schema",
        primaryKey: 'id',
        type: "object",
        properties: {
            id: {
                type: "string",
                maxLength: 150
            },
            formId: {
                type: "string"
            },
            formVersion: {
                type: "number"
            },
            userId: {
                type: "string"
            },
        }
    };

    private readonly surveySchema = {
        title: "survey schema",
        version: RxdbInitializerService.VERSION,
        description: "survey schema",
        primaryKey: 'id',
        type: "object",
        properties: {
            id: {
                type: "string",
                maxLength: 150
            },
            formId: {
                type: "string"
            },
            formVersion: {
                type: "number"
            },
            userId: {
                type: "string"
            },
        }
    };

    private readonly productsEntitlementsSchema = {
        title: "Product entitles schema",
        version: RxdbInitializerService.VERSION,
        description: "Products entitles storage",
        primaryKey: 'id',
        type: "object",
        properties: {
            id: {
                type: "string",
                maxLength: 150
            },
            applicationId: {
                type: "string"
            },
            formId: {
                type: "string"
            },
            formVersion: {
                type: "number"
            },
            userId: {
                type: "string"
            },
        }
    };

    private readonly productsMenuSchema = {
        title: "Product menu schema",
        version: RxdbInitializerService.VERSION,
        description: "Products menu storage",
        primaryKey: 'id',
        type: "object",
        properties: {
            id: {
                type: "string",
                maxLength: 150
            },
            formId: {
                type: "string"
            },
            formVersion: {
                type: "number"
            },
            userId: {
                type: "string"
            },
        }
    };

    private readonly productsSchema = {
        title: "Product schema",
        version: RxdbInitializerService.VERSION,
        description: "Products storage",
        primaryKey: 'id',
        type: "object",
        properties: {
            id: {
                type: "string",
                maxLength: 150
            },
            productId: {
                type: "string"
            },
            version: {
                type: "number"
            },
            name: {
                type: "string"
            },
            userId: {
                type: "string"
            },
        }
    };

    private readonly validatorSchema = {
        title: "validator schema",
        version: RxdbInitializerService.VERSION,
        description: "validator storage",
        primaryKey: 'id',
        type: "object",
        properties: {
            id: {
                type: "string",
                maxLength: 150
            },
            validatorSyncVersion: {
                type: "number"
            }
        }
    };

    private readonly currentSessionUserSchema = {
        title: "current session user schema",
        version: RxdbInitializerService.VERSION,
        description: "current session user storage",
        primaryKey: 'id',
        type: "object",
        properties: {
            id: {
                type: "string",
                maxLength: 150
            }
        }
    };

    private readonly offlineApplicationSchema = {
        title: "offline application schema",
        version: RxdbInitializerService.VERSION,
        description: "Offline application storage",
        primaryKey: 'offlineApplicationId',
        type: "object",
        properties: {
            offlineApplicationId: {
                type: "string",
                maxLength: 150
            },
            userId: {
                type: "string"
            },
        }
    };

    private readonly uploadFileSchema = {
        title: "upload file schema",
        version: RxdbInitializerService.VERSION,
        description: "upload file storage",
        primaryKey: 'applicationId',
        type: "object",
        properties: {
            applicationId: {
                type: "string",
                maxLength: 150
            }
        }
    };

    private readonly vaultProductsSchema = {
        title: "vault products schema",
        version: RxdbInitializerService.VERSION,
        description: "vault products schema",
        primaryKey: 'id',
        type: "object",
        properties: {
            id: {
                type: "string",
                maxLength: 150
            },
            productId: {
                type: "string"
            },
            userId: {
                type: "string"
            },
        }
    };

    private readonly externalPopulatedFieldsSchema = {
        title: "external populated fields schema",
        version: RxdbInitializerService.VERSION,
        description: "external populated fields schema",
        primaryKey: 'id',
        type: "object",
        properties: {
            id: {
                type: "string",
                maxLength: 150
            },
            formId: {
                type: "string"
            },
            formVersion: {
                type: "number"
            },
            userId: {
                type: "string"
            },
        }
    };

    readonly collectionMap = [
        { name: this.applicationEmptyDbColl, schema: this.applicationEmptySchema },
        { name: this.currentSessionUserColl, schema: this.currentSessionUserSchema },
        { name: this.externalPopulatedFieldsColl, schema: this.externalPopulatedFieldsSchema },
        { name: this.organisationDbColl, schema: this.organisationSchema },
        { name: this.productEntitlementsColl, schema: this.productsEntitlementsSchema },
        { name: this.productMenuColl, schema: this.productsMenuSchema },
        { name: this.productsColl, schema: this.productsSchema },
        { name: this.offlineApplicationColl, schema: this.offlineApplicationSchema },
        { name: this.surveyColl, schema: this.surveySchema },
        { name: this.uploadFileDbColl, schema: this.uploadFileSchema },
        { name: this.validatorsColl, schema: this.validatorSchema },
        { name: this.vaultProductColl, schema: this.vaultProductsSchema },
    ];

}
